import * as React from "react"
import { useLogInEmailMutation } from "@digits-graphql/frontend/graphql-public"
import { SvgMail01 } from "@digits-shared/components/SVGIcons/line/Mail01.svg"
import { DigitsButton } from "@digits-shared/DesignSystem/Button"
import { Input } from "@digits-shared/DesignSystem/FormElements/Input"
import useRouter from "@digits-shared/hooks/useRouter"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { useSignIn } from "src/frontend/components/Public/hooks/useSignIn"
import { useGoToSignInHome } from "src/frontend/components/Public/LogIn/Glow/useGoToSignInHome"
import { useSendMagicLink } from "src/frontend/components/Public/LogIn/Glow/useSendMagicLink"
import { useOnboardingContext } from "src/frontend/components/Public/Onboarding/OnboardingContext"
import { EmailSignInField } from "src/frontend/components/Public/Onboarding/reducer"
import { ButtonWrapper } from "src/frontend/components/Public/Shared/RegistrationElements"
import { Header } from "src/frontend/components/Shared/Glow/Header"
import type FrontendSession from "src/frontend/session"

/*
  STYLES
*/

const Caption = styled.div`
  text-align: center;
  font-size: 10px;
  color: ${colors.secondary};
  margin-top: 8px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const InputContainer = styled.div`
  display: grid;
`

const ForgotPassword = styled.button`
  display: block;
  justify-self: end;
  margin-top: 1px;
  padding: 0;
  border: 0;
  font-size: 10px;
  background: unset;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
`

/*
  COMPONENTS
*/

export const EmailSignIn: React.FC = () => {
  const {
    state: { emailAddress, loading },
    isFieldInvalid,
    onFieldChanged,
    onFieldBlurred,
  } = useOnboardingContext()

  const { onMagicLinkClick, magicLinkLoading } = useMagicLink()
  const { onSignInClick, isSignInDisabled, logInLoading } = useSignInForm()
  const onForgotPasswordClick = useForgotPasswordClick()
  const goHome = useGoToSignInHome()
  const mfaHidden = emailAddress?.split("@")[1] === process.env.MFA_DOMAIN

  return (
    <>
      <Header onBack={goHome} disabled={loading}>
        Sign in to Digits
      </Header>
      <Form onSubmit={onSignInClick}>
        <Input
          hidden
          id={EmailSignInField.EmailAddress}
          name={EmailSignInField.EmailAddress}
          hideLabel
          label="Enter email"
          type="email"
          autoComplete="email"
          disabled
          value={emailAddress}
        />
        {!mfaHidden && (
          <>
            <InputContainer>
              <Input
                autoFocus
                id={EmailSignInField.Password}
                name={EmailSignInField.Password}
                placeholder="Enter password"
                label="Enter password"
                hideLabel
                type="password"
                autoComplete="current-password"
                onChange={onFieldChanged}
                onBlur={onFieldBlurred}
                invalid={isFieldInvalid(EmailSignInField.Password)}
                disabled={loading}
              />
              <ForgotPassword type="button" onClick={onForgotPasswordClick} disabled={loading}>
                Forgot password?
              </ForgotPassword>
            </InputContainer>
            <ButtonWrapper disabled={isSignInDisabled}>
              <DigitsButton
                $fullWidth
                $solidIcon
                type="submit"
                id="email-sign-in-button"
                disabled={isSignInDisabled}
                label="Sign In"
                loading={logInLoading}
              />
            </ButtonWrapper>
          </>
        )}
      </Form>
      <div>
        <DigitsButton
          $fullWidth
          $variant="secondary-dark"
          disabled={loading}
          loading={magicLinkLoading}
          id="magic-link-send-button"
          onClick={onMagicLinkClick}
          aria-label="Send Me a Link to Sign In"
          label="Send Me a Link to Sign In"
          leftDecorator={<SvgMail01 />}
        />
        <Caption>We'll email you a secure, one-time link for password-free sign in.</Caption>
      </div>
    </>
  )
}

function useMagicLink() {
  const { location } = useRouter()
  const redirectPath = location.state?.from?.pathname
  const {
    state: { emailAddress },
    dispatch,
  } = useOnboardingContext()
  const onCompleted = React.useCallback(() => {
    dispatch({
      type: "SHOW_MAGIC_LINK_SENT",
      emailAddress,
      redirectPath,
    })
  }, [dispatch, emailAddress, redirectPath])
  const magicLinkInitializers = React.useMemo(
    () => ({ redirectPath, onCompleted }),
    [redirectPath, onCompleted]
  )

  return useSendMagicLink(magicLinkInitializers)
}

function useSignInForm() {
  const session = useSession<FrontendSession>()
  const activeLegalEntityId = session.lastKnownLegalEntityId
  const { onSignInCompleted, onSignInError } = useSignIn()
  const {
    state: { emailAddress, loading },
    dispatch,
    fieldHasValidValue,
    getFieldValue,
  } = useOnboardingContext()

  const [logIn, { loading: logInLoading }] = useLogInEmailMutation({
    onCompleted: onSignInCompleted,
    onError: onSignInError,
    context: { publicAPI: true },
  })

  const isSignInDisabled = React.useMemo(
    () => logInLoading || loading || !fieldHasValidValue(EmailSignInField.Password),
    [fieldHasValidValue, loading, logInLoading]
  )

  const onSignInClick = React.useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const password = getFieldValue(EmailSignInField.Password)
      dispatch({ type: "SET_LOADING", loading: true })

      logIn({
        variables: {
          emailAddress: emailAddress || "",
          password: password || "",
          activeLegalEntityId,
        },
      })
    },
    [activeLegalEntityId, dispatch, emailAddress, getFieldValue, logIn]
  )

  return React.useMemo(
    () => ({
      onSignInClick,
      isSignInDisabled,
      logInLoading,
    }),
    [onSignInClick, isSignInDisabled, logInLoading]
  )
}

function useForgotPasswordClick() {
  const { dispatch } = useOnboardingContext()
  return React.useCallback(
    (e: React.FormEvent) => {
      // must preventDefault due to form nesting
      e.preventDefault()
      dispatch({ type: "SHOW_FORGOT_PASSWORD" })
    },
    [dispatch]
  )
}
